/**
 * NotesScratchpad — a free-text pad the recruiter types into while talking.
 * Saving posts an internal note to the contact timeline (POST /contacts/:id/notes).
 *
 * The draft lives only here until it is saved, so anything that is about to
 * close Call Mode on the recruiter's behalf (logging an outreach outcome) must
 * save it first: `onApiReady` hands the parent a `flush()` for exactly that.
 *
 * A draft belongs to the person it was typed for. Call Mode does NOT remount
 * when the next call re-points it at someone else, so the pad can outlive the
 * person on screen: when that happens the draft is saved to the PREVIOUS
 * person's record and the pad starts empty for the new one. A draft is never
 * posted to whoever the pad happens to show when the save runs — that would put
 * one patient's call notes on another patient's timeline, silently, because the
 * outcome flush saves without a click. If saving to the previous person fails,
 * their notes stay on screen, marked as theirs, until they save or are
 * discarded by hand.
 *
 * The pad is a mention input (mentions/MentionInput): "@" names a teammate, and
 * a save sends the teammates whose "@Name" is still in the text as `mentions`
 * (mentions/api createNote; none = exactly today's { body }). Mentions travel
 * with the draft they were typed in: a previous person's carried-over notes
 * tell the same teammates. When a note saves but its mentions could not be
 * sent (`mentions: { failed: true }`), a line under the pad says so until the
 * next save.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Loader2 } from 'lucide-react';
import MentionInput, { namedAtPost } from '../../mentions/MentionInput';
import { createNote } from '../../mentions/api';
import { notifyFailedWords } from '../../mentions/mentionLogic';
import type { MentionableUser } from '../../mentions/types';

export interface NotesPadApi {
  /**
   * Save whatever is typed and not yet saved (nothing typed = nothing to do),
   * including notes left over from the previous person on the call.
   * Resolves true when no unsaved draft is left, false when a save failed —
   * the draft then stays in the pad with the error under it.
   */
  flush: () => Promise<boolean>;
}

interface Props {
  contactId: string;
  /** The person's name when known — only to label their notes if they have to be carried over. */
  contactName?: string;
  /** Bump the parent so the timeline/cadence refetches after a note lands. */
  onSaved?: () => void;
  /** Registers the pad's handle (null on unmount), like the prescreen pane's. */
  onApiReady?: (api: NotesPadApi | null) => void;
}

/** Who a draft was typed for. A fresh object per draft, so a save can tell whether the pad still holds it. */
interface Draft {
  contactId: string;
  name?: string;
  /** Why its last save failed — read when the pad has moved on and the carried-over entry reports it. */
  lastError?: string;
}

/** A previous person's notes the pad could not save when it moved on to someone else. */
interface Carried {
  id: number;
  contactId: string;
  name?: string;
  body: string;
  /** The teammates those notes name ("@Name" picked in the pad): sent with them, whoever the pad shows now. */
  named: MentionableUser[];
  saving: boolean;
  error: string | null;
}

const SAVE_FAILED = 'Could not save the note.';

const errorText = (e: any): string => e?.response?.data?.error || SAVE_FAILED;

/** Post a note to that person's record; `named` go as `mentions` (none = exactly today's { body }). */
const postNote = (contactId: string, body: string, named: ReadonlyArray<MentionableUser>) =>
  createNote(contactId, { body, mentions: named.map((u) => u.id) });

export default function NotesScratchpad({ contactId, contactName, onSaved, onApiReady }: Props) {
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // A note saved but its mentions could not be sent: "Saved; we could not tell Dana. ..."
  const [notice, setNotice] = useState<string | null>(null);
  const [carried, setCarried] = useState<Carried[]>([]);
  // The teammates picked with "@" for the draft in the pad, read through the ref by
  // saves that run outside the render that made them (flush, the move to the next person).
  const [picked, setPickedState] = useState<MentionableUser[]>([]);
  const pickedRef = useRef<MentionableUser[]>(picked);
  const setPicked = useCallback((next: MentionableUser[]) => {
    pickedRef.current = next;
    setPickedState(next);
  }, []);

  // flush() is called from outside the render that created it, so it reads
  // the draft (and a save already under way) through refs, never a stale copy.
  const textRef = useRef(text);
  textRef.current = text;
  // Bound at the first keystroke, cleared when the pad empties or the draft
  // saves. A save posts to THIS, never to the contact the pad shows now.
  const draftRef = useRef<Draft | null>(null);
  const inFlight = useRef<{ draft: Draft; run: Promise<boolean> } | null>(null);
  const carriedRef = useRef<Carried[]>([]);
  const carriedSaves = useRef(new Map<number, Promise<boolean>>());
  const nextCarriedId = useRef(1);
  const nameRef = useRef(contactName);
  nameRef.current = contactName;

  const updateCarried = useCallback((fn: (list: Carried[]) => Carried[]) => {
    carriedRef.current = fn(carriedRef.current);
    setCarried(carriedRef.current);
  }, []);

  const addCarried = useCallback((draft: Draft, body: string, named: MentionableUser[]) => {
    const item: Carried = { id: nextCarriedId.current, contactId: draft.contactId, name: draft.name, body, named, saving: true, error: null };
    nextCarriedId.current += 1;
    updateCarried((list) => [...list, item]);
    return item;
  }, [updateCarried]);

  /**
   * Follow one save of a carried-over entry (null = saved, else why not): saved, it leaves the
   * list; failed, it stays with its error. Registered while it runs, so flush() and a second
   * "Try again" wait for it instead of posting the note again.
   */
  const settleCarried = useCallback((id: number, outcome: Promise<string | null>): Promise<boolean> => {
    const done = outcome.then((err) => {
      if (err === null) updateCarried((list) => list.filter((c) => c.id !== id));
      else updateCarried((list) => list.map((c) => (c.id === id ? { ...c, saving: false, error: err } : c)));
      carriedSaves.current.delete(id);
      return err === null;
    });
    carriedSaves.current.set(id, done);
    return done;
  }, [updateCarried]);

  /** Save (or re-try) a previous person's notes — always to that person's record. */
  const saveCarried = useCallback((id: number): Promise<boolean> => {
    const running = carriedSaves.current.get(id);
    if (running) return running;
    const item = carriedRef.current.find((c) => c.id === id);
    if (!item) return Promise.resolve(true);
    updateCarried((list) => list.map((c) => (c.id === id ? { ...c, saving: true, error: null } : c)));
    return settleCarried(id, Promise.resolve()
      .then(() => postNote(item.contactId, item.body, item.named))
      .then((res) => {
        // Saved on their record, but the teammates it names could not be told.
        if (res.mentions.failed) setNotice(notifyFailedWords(item.named));
        return null;
      }, (e: any) => errorText(e)));
  }, [updateCarried, settleCarried]);

  const discardCarried = useCallback((id: number) => {
    updateCarried((list) => list.filter((c) => c.id !== id));
  }, [updateCarried]);

  const save = useCallback((): Promise<boolean> => {
    // A save of this same draft already under way (the recruiter hit Save,
    // then logged the outcome) is the same save — never post the note twice.
    if (inFlight.current && inFlight.current.draft === draftRef.current) return inFlight.current.run;
    const body = textRef.current.trim();
    if (!body) return Promise.resolve(true);
    const draft = draftRef.current ?? { contactId, name: nameRef.current };
    draftRef.current = draft;
    // The teammates the note names as it is saved ("@Name" still in the text).
    const named = namedAtPost(body, pickedRef.current).users;
    setSaving(true);
    setError(null);
    setNotice(null);
    const run = (async () => {
      try {
        const res = await postNote(draft.contactId, body, named);
        // The pad may have moved on to the next person while this was saving;
        // then it holds their (new) draft, which is not this one to clear.
        if (draftRef.current === draft) {
          draftRef.current = null;
          setText('');
          setPicked([]);
          setSaved(true);
          setTimeout(() => setSaved(false), 2000);
          onSaved?.();
        }
        // Saved, but the teammates it names could not be told: say so.
        if (res.mentions.failed) setNotice(notifyFailedWords(named));
        return true;
      } catch (e: any) {
        // Still on screen: the error goes under the pad. Moved on: the pad
        // handed this save to its carried-over entry, which reports it there.
        draft.lastError = errorText(e);
        if (draftRef.current === draft) setError(draft.lastError);
        return false;
      } finally {
        // Only this draft's own save clears the marker: a save of the next
        // person's draft may have started while this one was running.
        if (inFlight.current?.draft === draft) {
          inFlight.current = null;
          setSaving(false);
        }
      }
    })();
    inFlight.current = { draft, run };
    return run;
  }, [contactId, onSaved, setPicked]);

  // The pad is pointed at someone else (the next call, or an inbound call
  // answered while Call Mode was still open): the draft typed so far is the
  // previous person's. Save it to THEIR record and start this person empty.
  const shownFor = useRef(contactId);
  useEffect(() => {
    if (shownFor.current === contactId) return;
    shownFor.current = contactId;
    const draft = draftRef.current;
    // Typed for this very person (back to them before the pad moved on): theirs to keep.
    if (draft && draft.contactId === contactId) return;
    const body = textRef.current.trim();
    // The teammates those notes name go with them; this person starts with nobody picked.
    const named = namedAtPost(body, pickedRef.current).users;
    const pending = inFlight.current;
    draftRef.current = null;
    textRef.current = '';
    setText('');
    setPicked([]);
    setError(null);
    setSaved(false);
    setSaving(false);
    if (!draft || !body) return;
    const item = addCarried(draft, body, named);
    // A save of that draft already running IS its save — its answer is this
    // entry's, so the note is never posted twice.
    if (pending && pending.draft === draft) {
      void settleCarried(item.id, pending.run.then((ok) => (ok ? null : draft.lastError || SAVE_FAILED)));
    } else {
      void saveCarried(item.id);
    }
  }, [contactId, addCarried, saveCarried, settleCarried, setPicked]);

  // Everything unsaved: this person's draft and every previous person's
  // carried-over notes (a save of theirs still running is waited for, not
  // repeated). Closing Call Mode on a true would lose nothing.
  const flush = useCallback(async (): Promise<boolean> => {
    const results = await Promise.all([save(), ...carriedRef.current.map((c) => saveCarried(c.id))]);
    return results.every(Boolean);
  }, [save, saveCarried]);

  const flushRef = useRef(flush);
  flushRef.current = flush;
  useEffect(() => {
    if (!onApiReady) return undefined;
    onApiReady({ flush: () => flushRef.current() });
    return () => onApiReady(null);
  }, [onApiReady]);

  const onType = (value: string) => {
    if (!value.trim()) {
      draftRef.current = null;
      // An emptied pad forgets its picks: a name typed again by hand is not a mention.
      if (pickedRef.current.length) setPicked([]);
    } else if (!draftRef.current) draftRef.current = { contactId, name: contactName };
    else if (!draftRef.current.name && contactName && draftRef.current.contactId === contactId) draftRef.current.name = contactName;
    textRef.current = value;
    setText(value);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* A previous person's notes: their own scrolling strip, so the pad (a few lines tall on
          desktop) keeps room to type this person's notes underneath. */}
      {carried.length > 0 && (
        <div className="mb-2 max-h-[60%] shrink-0 space-y-2 overflow-y-auto">
          {carried.map((c) => (
            <div
              key={c.id}
              role={c.error ? 'alert' : 'status'}
              className={`rounded-lg border p-2 text-xs ${c.error
                ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900/50 dark:bg-red-900/20 dark:text-red-300'
                : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300'}`}
            >
              {c.error ? (
                <p>
                  <span className="font-semibold">Your notes from the call with {c.name || 'the last person'} didn&apos;t save.</span>
                  {c.error !== SAVE_FAILED ? ` ${c.error}` : ''} They are kept here, apart from this person&apos;s notes —
                  &quot;Try again&quot; saves them to {c.name ? `${c.name}'s` : 'their'} record, not this one.
                </p>
              ) : (
                <p>Saving your notes from the call with {c.name || 'the last person'} to their record…</p>
              )}
              <p className="mt-1 max-h-16 overflow-y-auto whitespace-pre-wrap break-words rounded border border-gray-200 bg-white p-1.5 text-gray-800 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200">
                {c.body}
              </p>
              {c.error && (
                <div className="mt-1.5 flex gap-2">
                  <button
                    type="button"
                    onClick={() => { void saveCarried(c.id); }}
                    disabled={c.saving}
                    className="inline-flex min-h-[44px] items-center rounded-lg bg-amber-600 px-2.5 py-1 text-xs font-medium text-white transition-colors duration-150 hover:bg-amber-700 disabled:opacity-50 sm:min-h-0"
                  >
                    Try again
                  </button>
                  <button
                    type="button"
                    onClick={() => discardCarried(c.id)}
                    disabled={c.saving}
                    className="inline-flex min-h-[44px] items-center rounded-lg border border-red-300 bg-white px-2.5 py-1 text-xs font-medium text-red-700 transition-colors duration-150 hover:bg-red-100 disabled:opacity-50 dark:border-red-800 dark:bg-gray-800 dark:text-red-300 dark:hover:bg-red-900/30 sm:min-h-0"
                  >
                    Discard them
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* The pad: "@" names a teammate. The list opens where it fits (above the pad on
          desktop; kept on screen when the pad fills a phone) and Esc closes only the
          list, never Call Mode. The wrapper is the flex item that grows now. */}
      <MentionInput
        value={text}
        onChange={onType}
        picked={picked}
        onPickedChange={setPicked}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) { e.preventDefault(); void save(); } }}
        placeholder="Jot notes while you talk… (saves to the timeline as a note; type @ to mention a teammate)"
        wrapperClassName="flex min-h-0 flex-1 flex-col"
        className="min-h-0 flex-1 resize-none rounded-lg border border-gray-300 bg-white p-3 text-sm text-gray-900 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
      />
      {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
      {notice && <p role="status" className="mt-2 text-xs text-amber-600 dark:text-amber-400">{notice}</p>}
      <div className="mt-2 flex items-center justify-between">
        <span className="text-xs text-gray-400">⌘/Ctrl + Enter to save</span>
        <button
          type="button"
          onClick={() => { void save(); }}
          disabled={saving || !text.trim()}
          className="inline-flex items-center gap-1.5 rounded-lg bg-amber-600 px-3 py-1.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-amber-700 disabled:opacity-50"
        >
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : saved ? <Check className="h-4 w-4" /> : null}
          {saved ? 'Saved' : 'Save note'}
        </button>
      </div>
    </div>
  );
}
